import { useState } from "react";
import { format } from "date-fns";

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { LoggedInUser, UsersOrderData } from "@/types";
import SkeletonCard from "@/components/Dashboard/SkeletonCard";
import { Calendar } from "@/components/ui/calendar";

interface OverviewTabProps {
	usersData: LoggedInUser[];
	ordersData: UsersOrderData[];
	thisMonthsTotalRevenue: number;
	thisYearsTotalRevenue: number;
	isLoading: boolean;
	searchQuery: string;
}

const OverviewTab = ({
	usersData,
	ordersData,
	thisMonthsTotalRevenue,
	thisYearsTotalRevenue,
	isLoading,
	searchQuery,
}: OverviewTabProps) => {
	const [date, setDate] = useState<Date | undefined>(new Date());

	const handleDateSelect = (selectedDate: Date | undefined) => {
		setDate(selectedDate);
	};

	const filteredOrders = ordersData.filter((order) => {
		if (!searchQuery) return true;
		const searchLower = searchQuery.toLowerCase();
		return (
			order.user.fullName?.toLowerCase().includes(searchLower) ||
			format(new Date(order.collectionDate), "PPP")
				.toLowerCase()
				.includes(searchLower) ||
			order.totalPrice.toString().includes(searchLower)
		);
	});

	const recentOrders = filteredOrders.slice(0, 5);
	const activeOrders = filteredOrders.filter(
		(order) => order.confirmed && !order.delivered && !order.cancelled,
	).length;

	const ordersForSelectedDate = date
		? filteredOrders.filter(
				(order) =>
					format(new Date(order.collectionDate), "yyyy-MM-dd") ===
					format(date, "yyyy-MM-dd"),
			)
		: [];

	return (
		<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
			<Card>
				<CardHeader className="pb-2">
					{isLoading ? (
						<SkeletonCard />
					) : (
						<>
							<CardDescription>Active Orders</CardDescription>
							<CardTitle className="text-4xl">{activeOrders}</CardTitle>
						</>
					)}
				</CardHeader>
			</Card>

			<Card>
				<CardHeader className="pb-2">
					{isLoading ? (
						<SkeletonCard />
					) : (
						<>
							<CardDescription>Total Customers</CardDescription>
							<CardTitle className="text-4xl">{usersData.length}</CardTitle>
						</>
					)}
				</CardHeader>
			</Card>

			<Card>
				<CardHeader className="pb-2">
					{isLoading ? (
						<SkeletonCard />
					) : (
						<>
							<CardDescription>This Month</CardDescription>
							<CardTitle className="text-4xl">
								£{thisMonthsTotalRevenue.toFixed(2)}
							</CardTitle>
						</>
					)}
				</CardHeader>
			</Card>

			<Card>
				<CardHeader className="pb-2">
					{isLoading ? (
						<SkeletonCard />
					) : (
						<>
							<CardDescription>This Year</CardDescription>
							<CardTitle className="text-4xl">
								£{thisYearsTotalRevenue.toFixed(2)}
							</CardTitle>
						</>
					)}
				</CardHeader>
			</Card>

			<Card className="col-span-2">
				<CardHeader>
					<CardTitle>Recent Orders</CardTitle>
					<CardDescription>Latest 5 orders</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{recentOrders.map((order) => (
							<div
								key={order.id}
								className="flex items-center justify-between rounded-lg border p-4"
							>
								<div>
									<p className="font-medium">{order.user.fullName}</p>
									<p className="text-sm text-muted-foreground">
										{format(new Date(order.collectionDate), "PPP")}
									</p>
								</div>
								<div className="text-right">
									<p className="font-medium">£{order.totalPrice.toFixed(2)}</p>
									<p className="text-sm text-muted-foreground">
										{order.basketItems.length} items
									</p>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<Card className="col-span-2">
				<CardHeader>
					<CardTitle>Collection Calendar</CardTitle>
					<CardDescription>Upcoming collections</CardDescription>
				</CardHeader>
				<CardContent>
					<Calendar
						mode="single"
						selected={date}
						onSelect={handleDateSelect}
						className="rounded-md border"
					/>
					{date && ordersForSelectedDate.length > 0 && (
						<div className="mt-4 space-y-2">
							<h4 className="font-medium">Orders for {format(date, "PPP")}</h4>
							{ordersForSelectedDate.map((order) => (
								<div
									key={order.id}
									className="flex items-center justify-between rounded-lg border p-2"
								>
									<p className="text-sm">{order.user.fullName}</p>
									<p className="text-sm font-medium">
										£{order.totalPrice.toFixed(2)}
									</p>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default OverviewTab;
